import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { LoadingButton } from "@mui/lab";
import { Box, Button, IconButton, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import moment from "moment";
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { ReactComponent as NotesIcon } from "../../../../public/svgs/edit.svg";
import { AuthContext } from "../../../contexts/AuthContext";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { getUserEmail } from "../../../helpers/authUser";
import { updateDashboardFunc } from "../../../lib/helper";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard, DashboardNote, DashboardNoteType, RefreshDataEnum } from "../../../types/files";
import { NotesContent } from "../../modals/dashboard-notes/DashboardNoteDialog";
import { CommentaryTitle } from "../dashboard-notes/Commentary";
import useRoute from '../../../hooks/useRoute';

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 450,
        maxWidth: 500,
        height: 'fit-content',
        padding: 16,
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: 'all 300ms ease-in-out',
    },
    container: {
        display: 'block',
        width: '100%',
        height: '100%',
        padding: 8,
        borderRadius: 12,
        border: `1px solid ${theme.colors.neutral['300']}`,
        background: '#fff',
        transition: 'all 0.2s ease',
        breakInside: 'avoid',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...scrollbarStyle,
        "&:hover": {
            border: `1px solid ${theme.colors.primary['300']}`,
            boxShadow: '0px 1px 5px rgba(0, 0, 0, 0.12)',
        },
    },
    title: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
        wordBreak: 'break-word',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Poppins',
        fontSize: '1.2rem',
        fontWeight: '600',
        color: '#666666',
    },
    label: {
        fontFamily: 'Poppins',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        "&:hover": {
            textDecoration: 'underline',
        }
    },
    timestamp: {
        fontFamily: 'Inter',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: theme.colors.neutral['600'],
        textAlign: 'right',
    },
    inputField: {
        width: '100%',
        "& .MuiOutlinedInput-root": {
            width: '100%',
            padding: 'unset',
            "& > textarea": {
                fontFamily: 'Inter',
                fontSize: '1rem',
                padding: 'unset',
            },
        },
    },
    editIcon: {
        width: 24,
        height: 24,
        padding: 'unset',
        "& > .MuiSvgIcon-root": {
            fill: theme.colors.neutral['500'],
        }
    },
    cancelButton: {
        borderRadius: 40,
        borderColor: theme.colors.neutral['100'],
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    saveButton: {
        borderRadius: 40,
        backgroundColor: theme.colors.primary['600'],
        color: 'white',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
}));

const CommentaryContentCard: React.FC<{
    dashboard: Dashboard,
    newCommentary?: boolean,
}> = ({ dashboard, newCommentary }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const { isPublicView } = useContext(DashboardContext);
    const { redirectToDashboard } = useRoute();

    const [editable, setEditable] = useState<boolean>(false);
    const [saveReady, setSaveReady] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const inputValue = useRef<TextFieldProps>(null);

    const commentaryNote = useMemo(() => dashboard!.notes?.find(note => note.title === CommentaryTitle), [dashboard]);
    const commentaryNoteContent = useMemo(() => (JSON.parse(commentaryNote?.data || '{}') as NotesContent[])?.[1]?.data?.text || '', [commentaryNote]);

    const handleSave = useCallback(async (commentary: string) => {
        setLoading(true);

        const noteData = JSON.stringify([
            {type: 'header', data: {level: 2,  text: CommentaryTitle,}},
            {type: 'paragraph', data: {text: commentary}},
        ]);
        const newNote: DashboardNote = {
            id: uuidv4(),
            title: CommentaryTitle,
            data: noteData,
            type: DashboardNoteType.External,
            createdBy: getUserEmail(user),
            updatedBy: getUserEmail(user),
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        let oldNotes: DashboardNote[] = dashboard!.notes || [];
        let newNotes: DashboardNote[] = [];

        if (commentaryNote) {
            newNotes = oldNotes.map(note => {
                if (note.id === commentaryNote.id)
                    return newNote;
                return note;
            }) || [];
        } else {
            newNotes = [...oldNotes, newNote];
        }

        const dashboardUpdate: Dashboard = {
            ...dashboard!,
            notes: newNotes,
            refreshData: {
                lastUpdatedBy: 'user',
                shouldRefresh: true,
                modifiedData: RefreshDataEnum.Notes,
            }
        };

        updateDashboardFunc(dashboardUpdate).then(() => {
            setLoading(false);
            setSaveReady(false);
            setEditable(false);
        });
    }, [user, dashboard, commentaryNote]);

    useEffect(() => {
        if (newCommentary) {
            setEditable(true);
            setSaveReady(true);
        }
    // eslint-disable-next-line
    }, []);

    return (<>
        <Stack direction="column" className={classes.card} spacing={2} justifyContent="stretch">
            <Stack direction="row" spacing={1} alignItems="center" width="100%">
                <NotesIcon width={24} height={24} style={{ flexShrink: 0, fill: 'rgb(163, 167, 172)' }} />
                <Stack direction="row" alignItems="center" justifyContent="space-between" pl={1} width="100%">
                    <Typography className={classes.title}>
                        {`${newCommentary ? 'Add commentary' : 'Commentary'} for `}
                        <Typography component="span" className={classes.label}
                            onClick={() => redirectToDashboard(dashboard.id)}
                            sx={{ cursor: 'pointer' }}
                            display="inline">
                            {dashboard.title}
                        </Typography>
                    </Typography>
                    <Typography className={classes.timestamp} width="30%">
                        {`${moment(commentaryNote?.createdAt).fromNow()}`}
                    </Typography>
                </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" width="100%" pl={4}>
                <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
                    <Stack className={classes.container} width="100%">
                        <TextField variant="outlined"
                            className={classNames('no-border', classes.inputField)}
                            inputProps={{ style: { width: '100%' }, className: 'no-fade' }}
                            defaultValue={commentaryNoteContent}
                            placeholder={!isPublicView ? 'Add your comments here...' : ''}
                            onChange={() => setSaveReady(!!inputValue.current?.value)}
                            inputRef={inputValue}
                            disabled={isPublicView || !editable}
                            minRows={3}
                            maxRows={5}
                            multiline
                        />
                    </Stack>
                    {editable && (<>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" width="100%">
                            <Button variant="outlined" size="small"
                                className={classes.cancelButton}
                                onClick={() => setEditable(false)}
                            > {'Cancel'} </Button>
                            <LoadingButton variant="contained" size="small"
                                className={classes.saveButton}
                                loading={loading}
                                disabled={!saveReady}
                                onClick={() => handleSave?.(inputValue.current?.value as string)}
                            > {'Save'} </LoadingButton>
                        </Stack>
                    </>)}
                </Stack>
                {(!editable && !isPublicView) ? (
                    <IconButton className={classes.editIcon}
                            disableRipple
                        onClick={() => setEditable(true)}>
                        <EditOutlinedIcon />
                    </IconButton>
                ): (<Box width="26px" height="100%" />)}
            </Stack>
        </Stack>
    </>);
};

export default memo(CommentaryContentCard);
